import React, { useState } from "react";
import { connect } from "react-redux";
import {
	updateActiveSlide,
	updateSlides,
} from "../../../store/actions/updateSlideList";
import {
	updateAutoSave,
	updateLastSaved,
} from "../../../store/actions/updateAutoSave";
import styles from "./SlideList.module.css";
import {
	secondaryTextColor,
	titleM,
	fw400,
	fw500,
	titleL,
} from "../../../Global.module.css";
import DropMenu from "../../reusable/DropMenu/DropMenu";
import IconTextButton from "../../reusable/Button/IconTextButton";
import ShareIcon from "../../../assets/share.svg";
import Duplicate from "../../../assets/duplicate.svg";
import DustbinIcon from "../../../assets/dustbin.svg";
import UpArrow from "../../../assets/upArrow.svg";
import DownArrow from "../../../assets/downArrow.svg";
import Modal from "../../reusable/Modal/Modal";
import { useDebouncedCallback } from "use-debounce";
import LayoutSelector from "../LayoutSelector/LayoutSelector";
import { updateEditorState } from "../../../store/actions/updateEditorState";
import { v4 as uuidv4 } from "uuid";
import {
	deleteSlide as api_deleteSlide,
	addSlide as api_addSlide,
	moveSlide as api_moveSlide,
} from "../../../helpers/api";
import { UndoRedo } from "../../../helpers/undoRedo";
import { getLayoutImage } from "../getLayoutImage";
import MoreIcon from "../../../assets/more.svg";

const SlideList = (props) => {
	const [state, setState] = useState({
		showDropMenuSlideNumber: false,
		showModal: false,
		insertIndex: null,
	});

	const debounced = useDebouncedCallback(async (indexes) => {
		props.updateAutoSave({ saving: true });
		try {
			await api_moveSlide(props.slidesMetaData.id, indexes);
			props.updateAutoSave({ saving: false });
		} catch (err) {
			props.updateAutoSave({ saving: false });
		}
		props.updateLastSaved({
			lastSaved: Date.now(),
		});
	}, 3000);

	const addSlide = (index) => {
		props.updateEditorState(true);
		selectLayout(index);
	};

	const editSlide = (index) => {
		if (props.slideList.activeSlide !== index) {
			props.updateActiveSlide({ activeSlide: index });
			UndoRedo.updateActiveSlide(props.slideList.slides[index], index);
		}
	};

	const deleteSlide = async (id, index) => {
		let slides = props.slideList.slides;
		if (slides[index].id === id) {
			let slide = { ...slides[index] };
			props.updateAutoSave({ saving: true });
			slides.splice(index, 1);
			props.updateSlides({ slides });
			let activeSlide = index > 0 ? index - 1 : 0;
			props.updateActiveSlide({
				activeSlide,
			});
			UndoRedo.deleteSlide(slide, index);
			UndoRedo.updateActiveSlide(slides[activeSlide], activeSlide);
			closeDropMenu();
			try {
				await api_deleteSlide(props.slidesMetaData.id, id);
			} catch (error) {}
			props.updateAutoSave({ saving: false });
			props.updateLastSaved({
				lastSaved: Date.now(),
			});
		}
	};

	const duplicateSlide = async (insertIndex) => {
		props.updateAutoSave({ saving: true });
		let clickedIndex = insertIndex - 1;
		let slides = props.slideList.slides;
		let slide = slides[clickedIndex];
		slide = {
			...slide,
			id: uuidv4(),
		};
		if (insertIndex === slides.length) {
			slides.push(slide);
		} else if (insertIndex === 0) {
			slides.unshift(slide);
		} else {
			slides.splice(insertIndex, 0, slide);
		}
		props.updateSlides({ slides });
		props.updateActiveSlide({ activeSlide: insertIndex });
		UndoRedo.addSlide(slide, insertIndex);
		UndoRedo.updateActiveSlide(slides[insertIndex]);
		closeDropMenu();
		try {
			await api_addSlide(props.slidesMetaData.id, slide, insertIndex);
		} catch (error) {}
		props.updateAutoSave({ saving: false });
		props.updateLastSaved({
			lastSaved: Date.now(),
		});
		props.updateEditorState(true);
	};

	const moveSlide = async (indexes) => {
		let slides = props.slideList.slides;
		if (
			indexes.length === 2 &&
			slides.length >= 2 &&
			indexes[0] !== indexes[1]
		) {
			let temp = slides[indexes[0]];
			slides[indexes[0]] = slides[indexes[1]];
			slides[indexes[1]] = temp;
			props.updateSlides({ slides });
			props.updateActiveSlide({ activeSlide: indexes[0] });
			debounced.callback(indexes);
		}
		closeDropMenu();
	};

	const selectLayout = (index) => {
		setState({
			...state,
			showDropMenuSlideNumber: false,
			showModal: true,
			insertIndex: index,
		});
	};

	const closeModal = () => {
		setState({
			...state,
			showModal: false,
			insertIndex: null,
		});
	};

	const showDropMenu = (event, index) => {
		event.preventDefault();
		if (props.slideList.activeSlide !== index) {
			props.updateActiveSlide({ activeSlide: index });
		}
		setState((state) => {
			let val = state.showDropMenuSlideNumber === index ? false : index;
			return {
				...state,
				showDropMenuSlideNumber: val,
			};
		});
	};

	const closeDropMenu = () => {
		if (state.showDropMenuSlideNumber >= 0) {
			setState({
				...state,
				showDropMenuSlideNumber: false,
			});
		}
	};

	const slideDropMenu = (index, id) => (
		<DropMenu position="overlay" onClose={closeDropMenu}>
			<IconTextButton
				text="New Slide"
				icon={ShareIcon}
				action={() => addSlide(index + 1)}
			/>
			<IconTextButton
				text="Duplicate"
				icon={Duplicate}
				action={() => duplicateSlide(index + 1)}
			/>
			{index !== 0 && props.slideList.slides.length >= 2 ? (
				<IconTextButton
					text="Move Up"
					icon={UpArrow}
					action={() => moveSlide([index - 1, index])}
				/>
			) : null}
			{index !== props.slideList.slides.length - 1 &&
			props.slideList.slides.length >= 2 ? (
				<IconTextButton
					text="Move Down"
					icon={DownArrow}
					action={() => moveSlide([index + 1, index])}
				/>
			) : null}
			<IconTextButton
				text="Delete"
				icon={DustbinIcon}
				action={() => deleteSlide(id, index)}
			/>
		</DropMenu>
	);

	const getSlidePreviewClass = (index) =>
		props.slideList.activeSlide === index
			? `${styles.slidePreviewCard} ${styles.activeSlidePreviewCard}`
			: `${styles.slidePreviewCard}`;

	const slideNumberClass = `${styles.slideNumber} ${secondaryTextColor} ${titleM} ${fw400}`;

	const getSlideList = () => {
		let list = [];
		let slides = props.slideList.slides;
		let src;
		for (let i = 0; i < slides.length; i++) {
			src = slides[i].preview || getLayoutImage(slides[i].layout);
			list.push(
				<div key={i} className={styles.previewContainer}>
					<div
						className={getSlidePreviewClass(i)}
						onContextMenu={(e) => showDropMenu(e, i)}
					>
						<img
							className={styles.moreOption}
							src={MoreIcon}
							alt="More Icon"
							onClick={(e) => showDropMenu(e, i)}
						/>
						<p className={slideNumberClass}>{i + 1}</p>
						<img
							className={styles.preview}
							src={src}
							alt=""
							onClick={() => editSlide(i)}
						/>
					</div>
					{state.showDropMenuSlideNumber === i
						? slideDropMenu(i, slides[i].id)
						: null}
				</div>
			);
		}
		return list;
	};

	return (
		<div className={styles.slideList}>
			{state.showModal ? (
				<Modal>
					<LayoutSelector
						closeModal={closeModal}
						insertIndex={state.insertIndex}
					/>
				</Modal>
			) : null}
			{getSlideList()}
			<button
				className={`${styles.addSlideBtn} ${secondaryTextColor} ${titleL} ${fw500}`}
				onClick={() => addSlide(props.slideList.slides.length)}
			>
				+ New Slide
			</button>
		</div>
	);
};

const mapStateToProps = (state) => ({
	...state,
});
const mapDispatchToProps = (dispatch) => ({
	updateSlides: (payload) => dispatch(updateSlides(payload)),
	updateActiveSlide: (payload) => dispatch(updateActiveSlide(payload)),
	updateAutoSave: (payload) => dispatch(updateAutoSave(payload)),
	updateLastSaved: (payload) => dispatch(updateLastSaved(payload)),
	updateEditorState: (payload) => dispatch(updateEditorState(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SlideList);
